import { Palette, Check } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { TEMAS, temaHabilitado } from "../lib/tema";
import { TAMANOS, textoHabilitado } from "../lib/texto";
import { LETRAS, letraHabilitada } from "../lib/letra";
import { trackEvent } from "../lib/eventTracking";
import { MODULOS } from "../lib/modulos";
import SegmentedControl from "./foundational/SegmentedControl";

// Selector de tema de ambiente. Vive en la pantalla Ajustes (card completo)
// y, desde 2026-09-10, en el popover "Apariencia" del header con `compacto`:
// mismas filas y mismos handlers, sin card ni párrafos didácticos, para que
// cambiar color y letra quede a un tap desde cualquier pantalla.
export default function TemaSelector({ compacto = false }) {
  const { dark, toggle, tema, setTema, texto, setTexto, letra, setLetra } = useTheme();

  if (!temaHabilitado()) return null;

  const elegir = (id) => {
    if (id === tema) return;
    setTema(id);
    trackEvent('ajuste_tema', { modulo: MODULOS.PERFIL, payload: { tema: id } });
  };

  // Tamaño del texto (2026-09-08): fila hermana de los temas, mismo card.
  const elegirTexto = (id) => {
    if (id === texto) return;
    setTexto(id);
    trackEvent('ajuste_texto', { modulo: MODULOS.PERFIL, payload: { texto: id } });
  };

  // Tipo de letra (2026-09-08): tercera fila del mismo card.
  const elegirLetra = (id) => {
    if (id === letra) return;
    setLetra(id);
    trackEvent('ajuste_letra', { modulo: MODULOS.PERFIL, payload: { letra: id } });
  };

  return (
    <div className={compacto ? '' : 'rounded-[14px] border border-edge bg-paper p-4 mb-4'}>
      {!compacto && (
        <div className="flex items-center gap-2 mb-3">
          <Palette size={15} className="text-accent" strokeWidth={1.75} />
          <p className="text-11px font-semibold uppercase tracking-[0.08em] text-ink-subtle font-sans">
            Cómo se ve MiPAE
          </p>
        </div>
      )}
      {/* Claro/oscuro vive AQUÍ desde 2026-09-10: el toggle salió del header
          para dejarle el lugar al avatar (la puerta al panel). Es el mismo
          `toggle` del ThemeContext; cada tema trae su versión clara y oscura. */}
      <div className="py-1 mb-3">
        <SegmentedControl
          orientation="horizontal"
          size="sm"
          ariaLabel="Modo claro u oscuro"
          options={[{ value: 'claro', label: 'Claro' }, { value: 'oscuro', label: 'Oscuro' }]}
          value={dark ? 'oscuro' : 'claro'}
          onChange={(v) => { if ((v === 'oscuro') !== dark) toggle(); }}
        />
      </div>
      {!compacto && (
        <p className="text-13px text-ink font-sans mb-3">
          El ambiente de la app: papel, texto y botones. Los semáforos clínicos y el color de cada módulo no cambian.
        </p>
      )}
      <div role="radiogroup" aria-label="Tema" className={`flex flex-wrap ${compacto ? 'gap-2' : 'gap-3'}`}>
        {TEMAS.map((t) => {
          const seleccionado = t.id === tema;
          // m es el bloque claro u oscuro según el modo actual. Estos hex
          // SON el dato del catálogo que se está previsualizando, no color
          // crudo de UI: cada tema define su propio swatch.
          const m = dark ? t.oscuro : t.claro;
          return (
            <div key={t.id} className={`${compacto ? 'w-[52px]' : 'w-16'} flex flex-col items-center gap-1`}>
              <button
                type="button"
                role="radio"
                aria-checked={seleccionado}
                aria-label={t.nombre}
                onClick={() => elegir(t.id)}
                style={{ backgroundColor: m.paper, borderColor: m.edgeStrong }}
                className={`${compacto ? 'h-10 w-10' : 'h-11 w-11'} rounded-full border flex items-center justify-center transition-shadow motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2 ${
                  seleccionado ? 'ring-2 ring-offset-2 ring-offset-paper ring-ink' : ''
                }`}
              >
                <span
                  className="h-5 w-5 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: m.accent }}
                >
                  {seleccionado && <Check size={14} strokeWidth={2.5} style={{ color: m.paper }} />}
                </span>
              </button>
              <span className={`${compacto ? 'text-9px' : 'text-11px'} text-ink-muted font-sans text-center leading-tight`}>{t.nombre}</span>
            </div>
          );
        })}
      </div>

      {textoHabilitado() && (
        <div className="mt-4 pt-4 border-t border-edge">
          <p className="text-11px font-semibold uppercase tracking-[0.08em] text-ink-subtle font-sans mb-2">
            Tamaño del texto
          </p>
          <div className="py-1">
            <SegmentedControl
              orientation="horizontal"
              size="sm"
              ariaLabel="Tamaño del texto"
              options={TAMANOS.map((t) => ({ value: t.id, label: t.nombre }))}
              value={texto}
              onChange={elegirTexto}
            />
          </div>
          {!compacto && (
            <p className="text-13px text-ink-muted font-sans mt-2">
              Cambia solo el tamaño de las letras. Se aplica en toda la app.
            </p>
          )}
        </div>
      )}

      {letraHabilitada() && (
        <div className="mt-4 pt-4 border-t border-edge">
          <p className="text-11px font-semibold uppercase tracking-[0.08em] text-ink-subtle font-sans mb-2">
            Tipo de letra
          </p>
          <div role="radiogroup" aria-label="Tipo de letra" className="flex flex-wrap gap-2">
            {LETRAS.map((l) => {
              const seleccionada = l.id === letra;
              return (
                <button
                  key={l.id}
                  type="button"
                  role="radio"
                  aria-checked={seleccionada}
                  aria-label={l.nombre}
                  onClick={() => elegirLetra(l.id)}
                  // l.sans ES el dato del catálogo que se está previsualizando
                  // (no tipografía cruda de UI). El botón no carga la fuente:
                  // hasta elegirla se ve en el fallback del navegador, y así
                  // el catálogo no descarga 3 familias solo por abrir el perfil.
                  style={{ fontFamily: `"${l.sans}", system-ui, sans-serif` }}
                  className={`${compacto ? 'h-10 px-3' : 'h-11 px-4'} rounded-full border font-medium flex items-center gap-2 transition-colors motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2 ${
                    seleccionada ? 'bg-accent-bg border-accent-border text-accent' : 'bg-paper border-edge text-ink'
                  }`}
                >
                  <span className="text-16px">Aa</span>
                  <span className="text-13px">{l.nombre}</span>
                </button>
              );
            })}
          </div>
          {!compacto && (
            <p className="text-13px text-ink-muted font-sans mt-2">
              La letra de toda la app. Las opciones distintas de MiPAE se descargan la primera vez que las eliges.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
